import { foregroundFor, paletteFor, primaryPaletteRoles, siteBackgroundFor } from './siteCmsPalettes';

export const DEFAULT_TECH_STYLE = {
  version: 2,
  pageSurface: 'site',
  panelSurface: 'dark',
  secondarySurface: 'light',
  accentSurface: 'accent',
  contentWidth: 'standard',
};

const SURFACES = ['light', 'soft', 'dark', 'accent'];
const PAGE_SURFACES = ['site', ...SURFACES];
const SECONDARY_SURFACES = [...SURFACES, 'panel'];
const WIDTHS = ['compact', 'standard', 'wide'];

export const normalizeTechStyle = value => {
  const incoming = value && typeof value === 'object' ? value : {};
  const source = Number(incoming.version || 1) < 2 ? DEFAULT_TECH_STYLE : incoming;
  return {
    version: 2,
    pageSurface: PAGE_SURFACES.includes(source.pageSurface) ? source.pageSurface : DEFAULT_TECH_STYLE.pageSurface,
    panelSurface: SURFACES.includes(source.panelSurface) ? source.panelSurface : DEFAULT_TECH_STYLE.panelSurface,
    secondarySurface: SECONDARY_SURFACES.includes(source.secondarySurface) ? source.secondarySurface : DEFAULT_TECH_STYLE.secondarySurface,
    accentSurface: SURFACES.includes(source.accentSurface) ? source.accentSurface : DEFAULT_TECH_STYLE.accentSurface,
    contentWidth: WIDTHS.includes(source.contentWidth) ? source.contentWidth : DEFAULT_TECH_STYLE.contentWidth,
  };
};

const roleColour = (roles, role) => roles[role] || roles.light;

const fontStacks = typography => {
  if (typography === 'editorial') return { display: 'var(--font-playfair), Georgia, serif', body: 'var(--font-body), system-ui, sans-serif' };
  if (typography === 'clean') return { display: 'var(--font-manrope), Manrope, system-ui, sans-serif', body: 'var(--font-dm-sans), "DM Sans", system-ui, sans-serif' };
  return { display: 'var(--font-display), "Space Grotesk", system-ui, sans-serif', body: 'var(--font-body), "Source Sans 3", system-ui, sans-serif' };
};

const buttonRadius = shape => shape === 'square' ? '0px' : shape === 'pill' ? '999px' : '10px';
const cardRadius = shape => shape === 'square' ? '0px' : shape === 'rounded' ? '16px' : '28px';
const widthValue = value => value === 'compact' ? '900px' : value === 'wide' ? '1380px' : '1120px';

export function techPageVariables(siteStyle, requestedTech) {
  const tech = normalizeTechStyle(requestedTech || siteStyle?.tech);
  const palette = paletteFor(siteStyle);
  const roles = primaryPaletteRoles(palette);
  const pageBg = tech.pageSurface === 'site' ? siteBackgroundFor(siteStyle) : roleColour(roles, tech.pageSurface);
  const panelBg = roleColour(roles, tech.panelSurface);
  const accentBg = roleColour(roles, tech.accentSurface);
  const secondaryBg = tech.secondarySurface === 'panel'
    ? `color-mix(in srgb, ${panelBg} 92%, ${accentBg} 8%)`
    : roleColour(roles, tech.secondarySurface);
  const pageFg = foregroundFor(palette, pageBg);
  const panelFg = foregroundFor(palette, panelBg);
  const accentFg = foregroundFor(palette, accentBg);
  const secondaryReference = tech.secondarySurface === 'panel' ? panelBg : roleColour(roles, tech.secondarySurface);
  const secondaryFg = foregroundFor(palette, secondaryReference);
  const fonts = fontStacks(siteStyle?.typography);

  return {
    '--nx-tech-page-bg': pageBg,
    '--nx-tech-page-fg': pageFg,
    '--nx-tech-panel-bg': panelBg,
    '--nx-tech-panel-fg': panelFg,
    '--nx-tech-secondary-bg': secondaryBg,
    '--nx-tech-secondary-fg': secondaryFg,
    '--nx-tech-accent': accentBg,
    '--nx-tech-accent-fg': accentFg,
    '--nx-tech-border': `color-mix(in srgb, ${accentBg} 28%, ${panelBg})`,
    '--nx-tech-muted-page-fg': `color-mix(in srgb, ${pageFg} 68%, ${pageBg})`,
    '--nx-tech-muted-panel-fg': `color-mix(in srgb, ${panelFg} 68%, ${panelBg})`,
    '--nx-tech-muted-secondary-fg': `color-mix(in srgb, ${secondaryFg} 70%, ${secondaryReference})`,
    '--nx-tech-button-radius': buttonRadius(siteStyle?.buttons?.shape),
    '--nx-tech-card-radius': cardRadius(siteStyle?.cards?.shape),
    '--nx-tech-max-width': widthValue(tech.contentWidth),
    '--nx-tech-display-font': fonts.display,
    '--nx-tech-body-font': fonts.body,
    '--bg-page': pageBg,
    '--bg-white': panelBg,
    '--bg-soft': secondaryBg,
    '--text-primary': pageFg,
    '--text-secondary': `color-mix(in srgb, ${pageFg} 68%, ${pageBg})`,
    '--brand-gold': accentBg,
    '--brand-gold-light': accentBg,
    '--border-light': `color-mix(in srgb, ${accentBg} 28%, ${panelBg})`,
    '--public-primary': accentBg,
  };
}

export function techSurfacePreview(siteStyle, role, fallback = 'light') {
  const palette = paletteFor(siteStyle);
  const roles = primaryPaletteRoles(palette);
  const background = role === 'site' ? siteBackgroundFor(siteStyle) : roleColour(roles, role === 'panel' ? fallback : role);
  return { background, color: foregroundFor(palette, background) };
}
